import { PlusOutlined } from "@ant-design/icons";
import { Button, Table, message } from "antd";
import * as Sentry from "@sentry/browser";
import debounce from "lodash.debounce";
import React from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import { IField } from "../components/AdvancedSearchModal";
import { PageToolbar } from "../components/PageToolbar";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import ExportData from "../components/ExportData";
import ImportData from "../components/ImportData";
import entitlements from "../config/entitlements";
import { FileTypes, User } from "../helpers/shared-interfaces";
import { request , showTotalPagination } from "../helpers/utilities";
import { ALL_USERS_QUERY } from "../gql/users";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface State {
  isFiltering: boolean;
  users: User[];
  page: number;
  userCount: number;
  filteredUsers: User[];
  filterBy: string;
  isLoading: boolean;
  showDialog: boolean;
}


export function UserCountRender(): JSX.Element {
  return <span>{0}</span>;
}

export const columns = [
  {
    title: "User Name",
    dataIndex: "name",
    key: "User Name",
    width: "65%",
    render(text: string, record: User): JSX.Element {
      return (
        <NavLink to={`/users/${record.id}`} data-cy="test-user-details-link">
          {text}
        </NavLink>
      );
    },
  },
];

export function UserTableFactory(
  data: User[],
  count: number,
  page: number,
  nextPage: (page: number) => void,
  loading?: boolean,
): JSX.Element {
  const tempData: User[] = data.map((user: User): User => {
    return { ...user, key: user.id };
  });
  return (
    <Table
      columns={columns}
      dataSource={tempData}
      loading={loading}
      pagination={{
        current: page,
        defaultPageSize: 25,
        total: count,
        onChange: nextPage,
        showTotal: showTotalPagination,
      }}
    />
  );
}

export default class AllUsers extends React.Component<
  RouteComponentProps,
  State
> {
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      showDialog: false,
      filterBy: "",
      isLoading: true,

      users: [],
      page: 1,
      userCount: 0,
      filteredUsers: [],
      isFiltering: false,
    };

    this.redirectToNewUserPage = this.redirectToNewUserPage.bind(this);
    this.getAllUsers = this.getAllUsers.bind(this);
    this.filterUsers = this.filterUsers.bind(this);
    this.nextPage = this.nextPage.bind(this);
  }

  async filterUsers(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    e.persist();
    this.setState({ filterBy: e.target.value, isLoading: true });
    this.triggerDelayedSearch();
  }

  async getAllUsers(): Promise<void> {
    this.setState({ isLoading: true });
    try {
      const { data: { data } }: any = await request({
        query: ALL_USERS_QUERY,
        variables: {
          page: this.state.page ?? 1,
          q: this.state.filterBy,
        },
      });
      this.setState({
        users: data.users.results,
        userCount: data.users.count,
      });
    } catch (error) {
      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllUsers();
  }, DEBOUNCE_TIME);


  redirectToNewUserPage(): void {
    this.props.history.push("/users/new");
  }

  async componentDidMount(): Promise<void> {
    this.setState({ isLoading: true });
    await this.getAllUsers();
  }

  sortFieldsByNameAsc = (fields: IField[]) => {
    return fields.sort(function (a, b) {
      var textA = a.name.toUpperCase();
      var textB = b.name.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
  };

  nextPage(page: number) {
    this.setState({ page }, (): void => {
      this.getAllUsers();
    });
  }

  render(): JSX.Element | null {
    const { users, isFiltering, filteredUsers, isLoading, userCount, page } =
      this.state;
    return (
      <>
          <Card>
            <div className="deparments-table-container">
              <PageToolbar
                searchValue={this.state.filterBy}
                onSearchChange={this.filterUsers}
                searchPlaceholder="Filter by name..."
                actions={
                  <EntitledSection entitlement={entitlements.Users.create}>
                    <div style={{ display: "flex", columnGap: 8 }}>
                      <ImportData
                        model="User"
                        title="Import Users"
                        buttonText="Import Users"
                        fileType={FileTypes.users}
                      />
                      <ExportData
                        model={"user"}
                        dataArray={this.state.users}
                      />
                    </div>
                  </EntitledSection>
                }
                primaryAction={
                  <EntitledSection entitlement={entitlements.Users.create}>
                    <Button
                      type="primary"
                      onClick={this.redirectToNewUserPage}
                      data-cy="test-new-user-button"
                    >
                      <PlusOutlined /> Create User
                    </Button>
                  </EntitledSection>
                }
                totalCount={userCount}
                currentDataCount={users.length}
                page={page}
                pageSize={25}
                modelName="users"
              />
              {isFiltering
                ? UserTableFactory(filteredUsers, userCount, page, this.nextPage, isLoading)
                : UserTableFactory(users, userCount, page, this.nextPage, isLoading)}
            </div>
          </Card>
      </>
    );
  }
}
